import React, { useState } from "react";
import useSWR from "swr";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { fmtRel, fmtBytes } from "@/lib/format";
import {
    AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogFooter,
    AlertDialogTitle, AlertDialogDescription, AlertDialogAction, AlertDialogCancel,
} from "@/components/ui/alert-dialog";
import { HardDrive, ShieldAlert, AlertTriangle, Trash2, Eraser, CheckCircle2, XCircle, AlertCircle } from "lucide-react";

const fetcher = (u) => api.get(u).then((r) => r.data);

export const StorageConsistencyOverview = () => {
    const { data, mutate } = useSWR("/api/admin/storage-consistency/overview", fetcher, {
        refreshInterval: 60000,
    });
    const c = data?.counts || {};
    const issues = data?.issues || [];
    const totalChecked = data?.total_checked || 0;

    // Show critical + error + skipped clouds (operator must see why checks fail)
    const critical = issues.filter((i) => i.severity === "critical");
    const errored = issues.filter((i) => i.severity === "error" || i.severity === "skipped");
    const visible = [...critical, ...errored];
    const affected = issues.filter((i) => (i.orphaned_in_s3 || 0) > 0 || (i.missing_appdata || 0) > 0);
    const aggOrphans = affected.reduce((a, i) => a + (i.orphaned_in_s3 || 0), 0);
    const aggAppdata = affected.reduce((a, i) => a + (i.missing_appdata || 0), 0);
    const aggUserData = affected.reduce((a, i) => a + (i.missing_user_data_nonempty || 0), 0);

    const [dialogOpen, setDialogOpen] = useState(false);
    const [progress, setProgress] = useState(null); // {done,total}
    const [result, setResult] = useState(null);
    const [err, setErr] = useState(null);
    const running = !!progress && progress.done < progress.total;

    const applyBulk = async () => {
        setErr(null);
        const total = affected.length;
        setProgress({ done: 0, total });
        const agg = { deleted_objects: 0, deleted_bytes: 0, appdata_pruned: 0, delete_errors: 0, failed: 0, clouds: 0 };
        for (let idx = 0; idx < affected.length; idx += 1) {
            const cloudId = affected[idx].cloud_id;
            try {
                const r = await api.post(`/api/admin/clouds/${cloudId}/storage-consistency/repair`, { dry_run: false });
                if (r.data?.ok) {
                    agg.deleted_objects += r.data.deleted_objects || 0;
                    agg.deleted_bytes += r.data.deleted_bytes || 0;
                    agg.appdata_pruned += r.data.appdata_pruned || 0;
                    agg.delete_errors += r.data.delete_errors || 0;
                    agg.clouds += 1;
                } else { agg.failed += 1; }
            } catch (e) { agg.failed += 1; setErr(formatApiError(e)); }
            setProgress({ done: idx + 1, total });
        }
        setResult(agg);
        setDialogOpen(false);
        setProgress(null);
        await mutate();
    };

    const chip = (icon, val, cls, testid) => (
        <span className={`inline-flex items-center gap-1 font-mono ${cls}`} data-testid={testid}>
            {icon} {val}
        </span>
    );

    return (
        <div className="cm-card px-4 py-3 mt-px" data-testid="storage-consistency-overview-card">
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                <HardDrive size={14} strokeWidth={1.5} className="text-zinc-400" />
                <span className="text-xs font-medium text-zinc-200">Storage consistency (S3 ↔ DB)</span>
                <span className="text-[11px] text-zinc-600 font-mono">{totalChecked} checked</span>
                <div className="ml-auto flex items-center gap-2.5 text-xs">
                    {chip(<ShieldAlert size={12} />, c.critical || 0, (c.critical || 0) > 0 ? "text-red-400" : "text-zinc-600", "sc-overview-critical")}
                    {chip(<AlertTriangle size={12} />, c.warning || 0, (c.warning || 0) > 0 ? "text-amber-300" : "text-zinc-600", "sc-overview-warning")}
                    {chip(<Trash2 size={12} />, c.info || 0, "text-zinc-600", "sc-overview-info")}
                    <span className="text-zinc-700 font-mono" data-testid="sc-overview-error">err {c.error || 0}</span>
                    {affected.length > 0 && (
                        <button
                            type="button"
                            data-testid="sc-overview-repair-all-button"
                            disabled={running}
                            onClick={() => { setResult(null); setDialogOpen(true); }}
                            className="inline-flex items-center gap-1 px-2 py-0.5 rounded border border-zinc-700 text-zinc-400 hover:text-sky-300 hover:border-sky-500/40 hover:bg-sky-500/10 disabled:opacity-40 transition-colors"
                            title="Clean up orphaned S3 objects + appdata/cache leftovers across all affected clouds"
                        >
                            <Eraser size={11} /> Clean up ({affected.length})
                        </button>
                    )}
                </div>
            </div>

            {result && (
                <div className="mt-2 text-xs font-mono rounded border border-emerald-500/30 bg-emerald-500/5 p-2 flex flex-wrap gap-x-4 gap-y-0.5" data-testid="sc-overview-repair-result">
                    <span className="text-emerald-300">✓ {result.clouds} clouds cleaned</span>
                    <span className="text-zinc-300">{result.deleted_objects.toLocaleString()} objects (~{fmtBytes(result.deleted_bytes)})</span>
                    <span className="text-zinc-300">{result.appdata_pruned.toLocaleString()} appdata entries</span>
                    {result.delete_errors > 0 && <span className="text-amber-300">{result.delete_errors} delete errors</span>}
                    {result.failed > 0 && <span className="text-red-400">{result.failed} clouds failed</span>}
                </div>
            )}
            {err && <div className="mt-2 text-xs text-cm-danger" data-testid="sc-overview-repair-error">{err}</div>}

            {visible.length === 0 ? (
                <div className="mt-1.5 flex items-center gap-1.5 text-xs text-zinc-500" data-testid="sc-overview-empty">
                    <CheckCircle2 size={12} className="text-emerald-500/70" />
                    {totalChecked === 0
                        ? "No clouds checked yet (check runs every 24 h)."
                        : "No critical inconsistencies."}
                    {(c.warning || 0) > 0 && (
                        <span className="text-zinc-600">· {c.warning} warning(s) — details per cloud.</span>
                    )}
                </div>
            ) : (
                <div className="mt-2 space-y-px bg-zinc-800 max-h-52 overflow-y-auto" data-testid="sc-overview-critical-list">
                    {visible.map((i) => {
                        const isError = i.severity === "error" || i.severity === "skipped";
                        const Icon = isError ? XCircle : ShieldAlert;
                        return (
                        <Link
                            key={i.cloud_id}
                            to={`/clouds/${i.cloud_id}`}
                            className="flex items-center gap-3 bg-zinc-950 hover:bg-zinc-900 px-2.5 py-1.5 transition-colors"
                            data-testid={`sc-overview-row-${i.cloud_id}`}
                        >
                            <Icon size={13} className="text-red-400 shrink-0" />
                            <span className="text-xs text-cm-brand truncate">
                                {i.primary_domain || i.fallback_hostname || `${i.cloud_id.slice(0, 8)}…`}
                            </span>
                            <span className="ml-auto font-mono text-xs shrink-0"
                                  style={{ color: isError ? '#f87171' : '#f87171' }}>
                                {isError
                                    ? (i.error || "Check failed")
                                    : `${i.missing_user_data_nonempty || 0} user data missing`}
                            </span>
                            <span className="font-mono text-[11px] text-zinc-600 shrink-0 w-12 text-right">
                                {i.last_run_at ? fmtRel(i.last_run_at) : "—"}
                            </span>
                        </Link>
                        );
                    })}
                </div>
            )}

            <AlertDialog open={dialogOpen} onOpenChange={(o) => { if (!running) setDialogOpen(o); }}>
                <AlertDialogContent data-testid="sc-overview-repair-dialog" className="bg-zinc-950 border-zinc-800">
                    <AlertDialogHeader>
                        <AlertDialogTitle className="flex items-center gap-2 text-zinc-100">
                            <Eraser size={16} className="text-sky-300" /> Clean up all affected clouds?
                        </AlertDialogTitle>
                        <AlertDialogDescription asChild>
                            <div className="text-xs text-zinc-400 space-y-2 pt-1">
                                <div className="font-mono space-y-1">
                                    <div className="flex justify-between"><span>Affected clouds</span><span className="text-zinc-200" data-testid="sc-overview-repair-clouds">{affected.length}</span></div>
                                    <div className="flex justify-between"><span>Orphaned S3 objects (total)</span><span className="text-sky-300" data-testid="sc-overview-repair-orphans">~{aggOrphans.toLocaleString()}</span></div>
                                    <div className="flex justify-between"><span>appdata/cache leftovers (total)</span><span className="text-zinc-300" data-testid="sc-overview-repair-appdata">~{aggAppdata.toLocaleString()}</span></div>
                                </div>
                                {aggUserData > 0 && (
                                    <div className="text-amber-300 bg-amber-500/10 border border-amber-500/30 rounded p-2">
                                        ⚠ {aggUserData} missing user-data entries will NOT be touched (data loss — review manually).
                                    </div>
                                )}
                                <div className="text-zinc-500">
                                    Per cloud the diff is recomputed live; only objects older than 24 h are deleted (in-flight protection). Irreversible.
                                </div>
                                {running && (
                                    <div className="text-sky-300" data-testid="sc-overview-repair-progress">
                                        Processing {progress.done}/{progress.total} clouds…
                                    </div>
                                )}
                            </div>
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel data-testid="sc-overview-repair-cancel-button" disabled={running} className="bg-zinc-800 border-zinc-700 text-zinc-200 hover:bg-zinc-700">Cancel</AlertDialogCancel>
                        <AlertDialogAction
                            data-testid="sc-overview-repair-confirm-button"
                            disabled={running}
                            onClick={(e) => { e.preventDefault(); applyBulk(); }}
                            className="bg-sky-600 hover:bg-sky-500 text-white"
                        >
                            {running ? `deleting… (${progress.done}/${progress.total})` : "Clean all now"}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
};

export default StorageConsistencyOverview;
